/**
 * EA Diagram Generator (Orchestrator)
 *
 * Coordinates AI-generated SVG diagram creation for all EA tabs.
 * Uses Claude AI to transform spreadsheet data into visual diagrams.
 *
 * Following OOA/OOD principles:
 * - Strategy Pattern: Each tab has its own prompt builder
 * - Orchestrator Pattern: Coordinates prompt building, AI calls, and storage
 * - Single Responsibility: Only diagram generation orchestration
 */

import type { CanisterClient, BulkEAImport, ChatMessage, ChatResponse } from '../canister-client';
import { environment } from '../config/environment';
import { DiagramStore } from './diagram-store';
import { buildCapabilitiesPrompt } from './prompts/capabilities-prompt';
import { buildServicesPrompt } from './prompts/services-prompt';
import { buildApplicationsPrompt } from './prompts/applications-prompt';
import { buildIntegrationsPrompt } from './prompts/integrations-prompt';
import { buildProcessesPrompt } from './prompts/processes-prompt';
import { buildInfrastructurePrompt } from './prompts/infrastructure-prompt';
import { buildDataPrompt } from './prompts/data-prompt';

/** Result of generating all diagrams */
export interface GenerationResult {
    succeeded: string[];
    failed: Array<{ tab: string; error: string }>;
    totalTokens: number;
}

/** Progress callback signature */
export type ProgressCallback = (tab: string, index: number, total: number) => void;

/** Tab name to prompt builder mapping */
type PromptBuilder = (data: BulkEAImport) => ChatMessage[];

const TAB_PROMPT_BUILDERS: Record<string, PromptBuilder> = {
    capabilities: buildCapabilitiesPrompt,
    services: buildServicesPrompt,
    applications: buildApplicationsPrompt,
    integrations: buildIntegrationsPrompt,
    processes: buildProcessesPrompt,
    infrastructure: buildInfrastructurePrompt,
    data: buildDataPrompt,
};

const DIAGRAM_TABS = Object.keys(TAB_PROMPT_BUILDERS);

/**
 * Orchestrator class that generates AI SVG diagrams for all EA tabs.
 */
export class DiagramGenerator {
    private canisterClient: CanisterClient;
    private diagramStore: DiagramStore;

    constructor(canisterClient: CanisterClient) {
        this.canisterClient = canisterClient;
        this.diagramStore = new DiagramStore(canisterClient);
    }

    /**
     * Generate diagrams for all 7 EA tabs sequentially.
     * Sequential to avoid overwhelming the Claude proxy with concurrent calls.
     */
    async generateAll(
        data: BulkEAImport,
        onProgress?: ProgressCallback
    ): Promise<GenerationResult> {
        const result: GenerationResult = {
            succeeded: [],
            failed: [],
            totalTokens: 0,
        };

        for (let i = 0; i < DIAGRAM_TABS.length; i++) {
            const tab = DIAGRAM_TABS[i];
            onProgress?.(tab, i + 1, DIAGRAM_TABS.length);

            try {
                const tokens = await this.generateOne(tab, data);
                result.succeeded.push(tab);
                result.totalTokens += tokens;
            } catch (error) {
                console.error(`[DiagramGenerator] Failed to generate ${tab}:`, error);
                result.failed.push({ tab, error: String(error) });
            }
        }

        return result;
    }

    /**
     * Generate a single diagram for one tab.
     * Returns token count from the Claude response.
     */
    async generateOne(tabName: string, data: BulkEAImport): Promise<number> {
        const builder = TAB_PROMPT_BUILDERS[tabName];
        if (!builder) {
            throw new Error(`No prompt builder for tab: ${tabName}`);
        }

        const messages = builder(data);
        const svg = await this.callClaude(messages);
        const cleaned = this.extractSvg(svg);

        if (!cleaned) {
            throw new Error('Claude response did not contain valid SVG');
        }

        await this.diagramStore.store(tabName, cleaned, 0);
        return 0;
    }

    /**
     * Call Claude AI to generate SVG.
     *
     * Strategy (works in both local dev and mainnet):
     * 1. Local dev:  Python proxy at port 5001 (no CORS, no timeout limits)
     * 2. Mainnet:    Browser-direct to api.anthropic.com (CORS allowed from icp0.io)
     *
     * The Python proxy won't exist on mainnet, so the fetch will fail fast
     * and fall through to browser-direct automatically.
     */
    private async callClaude(messages: ChatMessage[]): Promise<string> {
        // Get API key from canister (needed for both paths)
        const apiKeyResult = await this.canisterClient.getApiKeyForFrontend();
        const apiKeyRes = apiKeyResult as unknown as { Ok?: string; Err?: string };
        if (!apiKeyRes.Ok) {
            throw new Error('No API key configured. Set Claude API key first.');
        }
        const apiKey = apiKeyRes.Ok;
        const formattedMessages = messages.map(m => ({ role: m.role, content: m.content }));

        // Path 1: Python proxy (local dev — fast fail if not running)
        try {
            console.log('[DiagramGenerator] Trying Python proxy (local dev)...');
            const response = await fetch(`${environment.proxyUrl}/claude/chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    api_key: apiKey,
                    max_tokens: 16384,
                    messages: formattedMessages,
                }),
            });

            if (!response.ok) {
                const body = await response.text().catch(() => '');
                throw new Error(`Proxy error: ${response.status} — ${body}`);
            }

            const data = await response.json();
            if (data.Err) throw new Error(data.Err);
            if (data.Ok?.message) {
                console.log('[DiagramGenerator] Python proxy succeeded, response length:', data.Ok.message.length);
                return data.Ok.message;
            }
            throw new Error('Unexpected response format from proxy');
        } catch (proxyError) {
            console.warn('[DiagramGenerator] Python proxy unavailable, trying browser-direct:', proxyError);
        }

        // Path 2: Browser-direct to Anthropic API (mainnet path)
        console.log('[DiagramGenerator] Calling Claude API directly from browser...');
        const response = await fetch('https://api.anthropic.com/v1/messages', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-api-key': apiKey,
                'anthropic-version': '2023-06-01',
                'anthropic-dangerous-direct-browser-access': 'true',
            },
            body: JSON.stringify({
                model: 'claude-sonnet-4-20250514',
                max_tokens: 16384,
                messages: formattedMessages,
            }),
        });

        if (!response.ok) {
            const body = await response.text().catch(() => '');
            throw new Error(`Claude API error: ${response.status} ${response.statusText} — ${body}`);
        }

        const data = await response.json();
        const textBlock = data.content?.find((b: { type: string }) => b.type === 'text');
        if (!textBlock?.text) {
            throw new Error('No text content in Claude response');
        }
        console.log('[DiagramGenerator] Browser-direct succeeded, response length:', textBlock.text.length);
        return textBlock.text;
    }

    /**
     * Extract SVG from Claude's response.
     * Strips markdown fences and any surrounding text.
     */
    private extractSvg(response: string): string | null {
        // Try to extract from markdown code fences
        const fenceMatch = response.match(/```(?:svg|xml)?\s*\n?([\s\S]*?)```/);
        if (fenceMatch) {
            const inner = fenceMatch[1].trim();
            if (inner.startsWith('<svg')) return inner;
        }

        // Try direct SVG extraction
        const svgMatch = response.match(/<svg[\s\S]*<\/svg>/);
        if (svgMatch) return svgMatch[0];

        return null;
    }
}
